/**
 * Network Status Indicator Component
 * 
 * Displays a banner when the user is offline and provides
 * visual feedback about network connectivity issues.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, Wifi, X } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Logger } from 'src/utils/logger';

const logger = Logger.createScopedLogger('NetworkStatus');

interface NetworkStatusIndicatorProps {
  className?: string;
  showOnlineConfirmation?: boolean;
  onlineConfirmationDuration?: number;
}

/**
 * Network Status Indicator that shows when user is offline
 */
const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  className,
  showOnlineConfirmation = true,
  onlineConfirmationDuration = 3000,
}) => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleOnline = useCallback(() => {
    logger.info('Network connection restored');
    setIsOnline(true);
    setIsDismissed(false);
    
    if (showOnlineConfirmation) {
      setShowOnlineMessage(true);
    }
  }, [showOnlineConfirmation]);

  const handleOffline = useCallback(() => {
    logger.warn('Network connection lost');
    setIsOnline(false);
    setIsDismissed(false);
    setShowOnlineMessage(false);
  }, []);

  // Handle online/offline events
  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // Auto-hide online confirmation message with proper cleanup
  useEffect(() => {
    if (!showOnlineMessage) return;

    const timer = setTimeout(() => {
      setShowOnlineMessage(false);
    }, onlineConfirmationDuration);

    return () => clearTimeout(timer);
  }, [showOnlineMessage, onlineConfirmationDuration]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // Don't render anything if online and not showing confirmation
  if (isOnline && !showOnlineMessage) {
    return null;
  }

  // Don't render if dismissed (only for offline state)
  if (!isOnline && isDismissed) {
    return null;
  }

  return (
    <div
      className={cn(
        'tw-fixed tw-bottom-4 tw-left-1/2 tw-z-50 tw--translate-x-1/2 tw-transform',
        'tw-animate-in tw-slide-in-from-bottom-4 tw-duration-300',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div
        className={cn(
          'tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-px-4 tw-py-3 tw-shadow-lg',
          'tw-text-sm tw-font-medium',
          isOnline
            ? 'tw-bg-green-600 tw-text-white'
            : 'tw-bg-gray-900 tw-text-white'
        )}
      >
        {isOnline ? (
          <>
            <Wifi className="tw-h-5 tw-w-5" />
            <span>
              {t('errors:network.restored', {
                defaultValue: 'Connection restored',
              })}
            </span>
          </>
        ) : (
          <>
            <WifiOff className="tw-h-5 tw-w-5" />
            <span>
              {t('errors:network.offline', {
                defaultValue: 'You are offline. Some features may be unavailable.',
              })}
            </span>
            <button
              onClick={handleDismiss}
              className="tw-ml-2 tw-rounded-full tw-p-1 tw-transition-colors hover:tw-bg-white/20"
              aria-label={t('actions:dismiss', { defaultValue: 'Dismiss' }) || 'Dismiss'}
            >
              <X className="tw-h-4 tw-w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Hook to check network status
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}

export { NetworkStatusIndicator };
export default NetworkStatusIndicator;
